import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { BookingService } from '@beautyapp/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NZ_DRAWER_DATA } from 'ng-zorro-antd/drawer';

interface AuditLog {
  id: string;
  action: 'CREATED' | 'UPDATED' | 'DELETED' | 'COMPLETED';
  changedBy: { fullName: string };
  timestamp: Date;
  oldValue: any;
  newValue: any;
}

interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

@Component({
  selector: 'beautyapp-booking-audit-history',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzTagModule,
    NzSpinModule,
    NzEmptyModule,
    NzButtonModule,
    NzIconModule,
    TranslateModule,
    DatePipe,
  ],
  templateUrl: './booking-audit-history.component.html',
  styleUrls: ['./booking-audit-history.component.scss'],
})
export class BookingAuditHistoryComponent implements OnInit, OnChanges {
  @Input() bookingId: string | null = null;

  private drawerData: { bookingId?: string } | null = inject(NZ_DRAWER_DATA, {
    optional: true,
  });

  auditLogs: AuditLog[] = [];
  loading = false;
  expandedRows = new Set<string>();

  fieldLabels: Record<string, string> = {
    bookingDate: 'თარიღი',
    startTime: 'დაწყება',
    endTime: 'დასრულება',
    status: 'სტატუსი',
    price: 'ფასი',
    salePrice: 'ფასდაკლება',
    employeeId: 'თანამშრომელი',
    userId: 'მომხმარებელი',
    description: 'აღწერა',
    cash: 'ნაღდი',
    card: 'ბარათი',
    balance: 'ბალანსი',
    barter: 'ბარტერი',
    bankTransfer: 'საბანკო გადარიცხვა',
    consignation: 'კონსიგნაცია',
    minutes: 'წუთები',
    isGift: 'საჩუქარი',
    sendNotification: 'შეტყობინება',
    onUserRequest: 'მომხმარებლის მოთხოვნით',
    salaryPercent: 'ხელფასი (%)',
    salaryFixed: 'ხელფასი (ფიქსირებული)',
  };

  private cdr = inject(ChangeDetectorRef);

  constructor(private bookingService: BookingService) {}

  ngOnInit(): void {
    // Support receiving bookingId from drawer data
    console.log('BookingAuditHistory - drawerData:', this.drawerData);
    console.log('BookingAuditHistory - Input bookingId:', this.bookingId);

    if (!this.bookingId && this.drawerData?.bookingId) {
      this.bookingId = this.drawerData.bookingId;
    }

    console.log('BookingAuditHistory - Final bookingId:', this.bookingId);
    this.loadAuditLogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bookingId'] && !changes['bookingId'].firstChange) {
      this.loadAuditLogs();
    }
  }

  loadAuditLogs(): void {
    if (!this.bookingId) {
      console.log('BookingAuditHistory - No bookingId, skipping load');
      return;
    }

    console.log(
      'BookingAuditHistory - Loading audit logs for:',
      this.bookingId
    );
    this.loading = true;
    this.bookingService.getAuditLog(this.bookingId).subscribe({
      next: (res) => {
        console.log('BookingAuditHistory - API response:', res);
        this.auditLogs = res?.auditLogs || [];
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('BookingAuditHistory - API error:', err);
        this.auditLogs = [];
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  toggleRow(id: string): void {
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expandedRows.has(id);
  }

  getActionLabel(action: string): string {
    const labels: Record<string, string> = {
      CREATED: 'შექმნა',
      UPDATED: 'რედაქტირება',
      DELETED: 'წაშლა',
      COMPLETED: 'დასრულება',
    };
    return labels[action] || action;
  }

  getActionColor(action: string): string {
    const colors: Record<string, string> = {
      CREATED: 'green',
      UPDATED: 'blue',
      DELETED: 'red',
      COMPLETED: 'purple',
    };
    return colors[action] || 'default';
  }

  getSummary(log: AuditLog): string {
    if (log.action === 'CREATED') return 'ახალი ჩანაწერი შეიქმნა';
    if (log.action === 'DELETED') return 'ჩანაწერი წაიშალა';
    if (log.action === 'COMPLETED') return 'ვიზიტი დასრულდა';

    const changes = this.getFieldChanges(log);
    if (changes.length === 0) return 'ცვლილებები არ არის';

    const changedFields = changes.map(
      (c) => this.fieldLabels[c.field] || c.field
    );
    return `შეიცვალა: ${changedFields.slice(0, 3).join(', ')}${
      changedFields.length > 3 ? '...' : ''
    }`;
  }

  getFieldChanges(log: AuditLog): FieldChange[] {
    if (!log.oldValue || !log.newValue) return [];

    const changes: FieldChange[] = [];
    const trackedFields = Object.keys(this.fieldLabels);

    for (const key of trackedFields) {
      const oldVal = log.oldValue?.[key];
      const newVal = log.newValue?.[key];

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    }

    // Track changes inside services array (nested salary fields)
    const oldServices = log.oldValue?.services || [];
    const newServices = log.newValue?.services || [];

    for (let i = 0; i < Math.max(oldServices.length, newServices.length); i++) {
      const oldService = oldServices[i];
      const newService = newServices[i];
      const serviceName =
        newService?.service?.name ||
        oldService?.service?.name ||
        `სერვისი ${i + 1}`;

      // Check salaryPercent change
      const oldSalaryPercent = oldService?.salaryPercent;
      const newSalaryPercent = newService?.salaryPercent;
      if (String(oldSalaryPercent) !== String(newSalaryPercent)) {
        changes.push({
          field: `სერვისის ხელფასი (%) - ${serviceName}`,
          oldValue: oldSalaryPercent,
          newValue: newSalaryPercent,
        });
      }

      // Check salaryFixed change
      const oldSalaryFixed = oldService?.salaryFixed;
      const newSalaryFixed = newService?.salaryFixed;
      if (
        String(oldSalaryFixed) !== String(newSalaryFixed) &&
        (oldSalaryFixed || newSalaryFixed)
      ) {
        changes.push({
          field: `სერვისის ხელფასი (ფიქს.) - ${serviceName}`,
          oldValue: oldSalaryFixed,
          newValue: newSalaryFixed,
        });
      }

      // Check service price change
      const oldPrice = oldService?.price;
      const newPrice = newService?.price;
      if (String(oldPrice) !== String(newPrice)) {
        changes.push({
          field: `სერვისის ფასი - ${serviceName}`,
          oldValue: oldPrice,
          newValue: newPrice,
        });
      }

      // Check expence change
      const oldExpence = oldService?.expence;
      const newExpence = newService?.expence;
      if (
        String(oldExpence) !== String(newExpence) &&
        (oldExpence || newExpence)
      ) {
        changes.push({
          field: `პროდუქტების ხარჯი - ${serviceName}`,
          oldValue: oldExpence,
          newValue: newExpence,
        });
      }

      // beaut-240: the additional cost is the one of the two a user can edit by
      // hand, so leaving it out meant change history recorded everything except
      // the field most likely to be changed.
      const oldAdditional = oldService?.additionalExpence;
      const newAdditional = newService?.additionalExpence;
      if (
        String(oldAdditional) !== String(newAdditional) &&
        (oldAdditional || newAdditional)
      ) {
        changes.push({
          field: `დამატებითი ხარჯი - ${serviceName}`,
          oldValue: oldAdditional,
          newValue: newAdditional,
        });
      }
    }

    return changes;
  }

  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'დიახ' : 'არა';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  hasChanges(log: AuditLog): boolean {
    return log.action !== 'CREATED' && this.getFieldChanges(log).length > 0;
  }
}
